import React, { useState, useEffect, useMemo, useRef, memo } from 'react'
import { withNamespaces } from 'react-i18next'
import { inject } from 'mobx-react'
import { withRouter } from 'react-router-dom'
import { BLOB_STORAGE } from 'config/app.config'
import LoadingGif from 'images/gifs/loading.gif'
import { Profile, Label } from '../styles'
import { CircleImage, UserLetter } from 'styles/components'
import i18n from 'i18next'
import ModalEmployee from './modalEmployee'
import { withNotification } from 'hoocs/index'
import { HooksModal } from 'components/HooksModal'
import * as M from './accountMenu.styles'

const LANGUAGES = [
  { code: 'pt-BR', key: 'sidebar.profile.portuguese', fallback: 'Português' },
  { code: 'en-US', key: 'sidebar.profile.english', fallback: 'English' },
  { code: 'es', key: 'sidebar.profile.spanish', fallback: 'Español' },
]

const normalize = (text) => `${text ?? ''}`.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12.5l4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Avatar = ({ employee, email, selectedTenant, size }) =>
  employee ? (
    <CircleImage
      src={`${BLOB_STORAGE}/${selectedTenant}/employee-${employee.id}.jpg`}
      fallbackImage={
        <UserLetter
          size={size}
          key={employee.id}
          letter={employee.shortName?.[0] ?? employee.fullName?.[0]}
        />
      }
      initialImage={LoadingGif}
      alt={employee.shortName}
      maxSize={size}
    />
  ) : (
    <UserLetter size={size} key={email} letter={(email && email[0]) || 'P'} />
  )

/**
 * Perfil na barra do topo e o menu da conta: ambiente atual (com troca e busca), idioma,
 * meu perfil, webhooks e sair. Mesmo visual dos menus do resto do app.
 */
const ProfileWrapper = ({
  t,
  rootStore,
  employee,
  reloadEmployee,
  successNotification,
  history,
}) => {
  const { email, selectedTenant, availableTenants, roles } = rootStore.authStore

  const isAdmin = roles.includes('admin') || roles.includes('superuser')
  const isStaging =
    window.location.hostname.includes('staging') || window.location.hostname.includes('localhost')

  const [menuOpen, setMenuOpen] = useState(false)
  const [view, setView] = useState('main')
  const [query, setQuery] = useState('')
  const [switching, setSwitching] = useState(null)
  const [profileOpen, setProfileOpen] = useState(false)
  const [hooksModalOpen, setHooksModalOpen] = useState(false)
  const box = useRef(null)
  const search = useRef(null)

  const tenants = useMemo(
    () =>
      (Array.isArray(availableTenants) ? availableTenants.slice() : []).sort((a, b) =>
        a.localeCompare(b)
      ),
    [availableTenants]
  )
  const filteredTenants = useMemo(
    () => tenants.filter((tenant) => normalize(tenant).includes(normalize(query))),
    [tenants, query]
  )
  const currentLanguage =
    LANGUAGES.find((language) => (i18n.language || '').startsWith(language.code.split('-')[0])) ||
    LANGUAGES[0]

  const close = () => {
    setMenuOpen(false)
    setView('main')
    setQuery('')
  }

  useEffect(() => {
    if (!menuOpen) return undefined
    const onDown = (event) => box.current && !box.current.contains(event.target) && close()
    const onKey = (event) => {
      if (event.key !== 'Escape') return
      if (view !== 'main') setView('main')
      else close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen, view])

  useEffect(() => {
    if (view === 'tenant' && search.current) search.current.focus()
  }, [view])

  const changeTenant = async (tenant) => {
    if (tenant === selectedTenant) return close()
    setSwitching(tenant)
    await rootStore.authStore.login(tenant)
    setTimeout(async () => {
      await rootStore.authStore.selectTenant(tenant)
    }, 1000)
  }

  const name = employee ? employee.shortName : email

  return (
    <Profile ref={box}>
      <M.Trigger
        type="button"
        onClick={() => (menuOpen ? close() : setMenuOpen(true))}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <span className="ps-profile-text">
          <Label>{name}</Label>
          <Label tenant>
            {isStaging && <span style={{ opacity: 0.7 }}>{'staging\u00a0·\u00a0'}</span>}
            {selectedTenant}
          </Label>
        </span>
        <Avatar employee={employee} email={email} selectedTenant={selectedTenant} size={36} />
      </M.Trigger>

      {menuOpen && (
        <M.Panel role="menu">
          {view === 'main' && (
            <>
              <M.Head>
                <Avatar
                  employee={employee}
                  email={email}
                  selectedTenant={selectedTenant}
                  size={40}
                />
                <div className="who">
                  <b>{employee ? employee.fullName || employee.shortName : email}</b>
                  {employee && <span>{email}</span>}
                </div>
              </M.Head>

              <M.Section>
                <M.Caption>{t('profileMenu.currentTenant', 'Ambiente atual')}</M.Caption>
                <M.TenantCard
                  type="button"
                  disabled={tenants.length < 2}
                  onClick={() => setView('tenant')}
                  title={t('sidebar.profile.changeTenant', 'Alterar ambiente')}
                >
                  <span className="name">
                    {selectedTenant}
                    {isStaging && <M.Chip>staging</M.Chip>}
                  </span>
                  {tenants.length > 1 && (
                    <span className="swap" aria-hidden="true">
                      <i className="fas fa-exchange-alt" />
                    </span>
                  )}
                </M.TenantCard>
              </M.Section>

              <M.Section>
                {employee && (
                  <M.Item
                    type="button"
                    onClick={() => {
                      close()
                      setProfileOpen(true)
                    }}
                  >
                    <i className="far fa-user" aria-hidden="true" />
                    <span className="label">{t('sidebar.profile.myProfile', 'Meu perfil')}</span>
                  </M.Item>
                )}
                {isAdmin && (
                  <M.Item
                    type="button"
                    onClick={() => {
                      close()
                      setHooksModalOpen(true)
                    }}
                  >
                    <i className="fas fa-plug" aria-hidden="true" />
                    <span className="label">{t('hooks.title', 'Webhooks')}</span>
                  </M.Item>
                )}
                <M.Item type="button" onClick={() => setView('language')}>
                  <i className="fas fa-globe" aria-hidden="true" />
                  <span className="label">{t('sidebar.profile.translate', 'Alterar idioma')}</span>
                  <span className="value">{t(currentLanguage.key, currentLanguage.fallback)}</span>
                  <i className="chevron fas fa-chevron-right" aria-hidden="true" />
                </M.Item>
              </M.Section>

              <M.Section>
                <M.Item
                  type="button"
                  onClick={() => {
                    close()
                    history.push('/logout')
                  }}
                >
                  <i className="fas fa-sign-out-alt" aria-hidden="true" />
                  <span className="label">{t('sidebar.profile.logout', 'Sair')}</span>
                </M.Item>
              </M.Section>
            </>
          )}

          {view === 'language' && (
            <>
              <M.Back type="button" onClick={() => setView('main')}>
                <i className="fas fa-arrow-left" aria-hidden="true" />
                {t('sidebar.profile.translate', 'Alterar idioma')}
              </M.Back>
              <M.Section>
                {LANGUAGES.map((language) => {
                  const selected = language.code === currentLanguage.code
                  return (
                    <M.Item
                      key={language.code}
                      type="button"
                      selected={selected}
                      onClick={() => {
                        i18n.changeLanguage(language.code)
                        setView('main')
                      }}
                    >
                      <span className="label">{t(language.key, language.fallback)}</span>
                      {selected && <Check />}
                    </M.Item>
                  )
                })}
              </M.Section>
            </>
          )}

          {view === 'tenant' && (
            <>
              <M.Back type="button" onClick={() => setView('main')}>
                <i className="fas fa-arrow-left" aria-hidden="true" />
                {t('sidebar.profile.changeTenant', 'Alterar ambiente')}
              </M.Back>
              <M.Search>
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  ref={search}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && filteredTenants.length === 1)
                      changeTenant(filteredTenants[0])
                  }}
                  placeholder={t('profileMenu.searchTenant', 'Buscar ambiente')}
                />
              </M.Search>
              <M.TenantList>
                {filteredTenants.map((tenant) => {
                  const selected = tenant === selectedTenant
                  return (
                    <M.Item
                      key={tenant}
                      type="button"
                      selected={selected}
                      disabled={!!switching}
                      onClick={() => changeTenant(tenant)}
                    >
                      <span className="label">{tenant}</span>
                      {switching === tenant && (
                        <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />
                      )}
                      {selected && <Check />}
                    </M.Item>
                  )
                })}
                {filteredTenants.length === 0 && (
                  <M.Empty>{t('profileMenu.noTenant', 'Nenhum ambiente encontrado')}</M.Empty>
                )}
              </M.TenantList>
            </>
          )}
        </M.Panel>
      )}

      {profileOpen && (
        <ModalEmployee
          open={profileOpen}
          closeFn={() => setProfileOpen(false)}
          editing={employee}
          updateEmployee={async () => {
            await reloadEmployee()
            setProfileOpen(false)
            successNotification('Perfil atualizado', 3000)
          }}
        />
      )}
      <HooksModal isOpen={hooksModalOpen} onClose={() => setHooksModalOpen(false)} />
    </Profile>
  )
}

export default withNamespaces()(
  withRouter(inject('rootStore')(memo(withNotification(ProfileWrapper))))
)
